import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["locationSelect", "cardsContainer"];
  static values = { phoneId: String, locationsByGroup: String };
  static outlets = ["multiselect"];

  connect() {
    if (!this.hasPhoneIdValue) return;

    if (this.hasLocationsByGroupValue) {
      this.locationsByGroup = JSON.parse(this.locationsByGroupValue);
    }
  }

  // Called when location multiselect change via change event
  handleLocationChange() {
    const selectedIds = this.multiselectOutlet.getSelectedValues();
    this.syncLocationCards(selectedIds);
  }

  handleLocationGroupChange(event) {
    const groupId = event.target.value;
    const locations = groupId
      ? this.locationsByGroup[groupId] || []
      : Object.values(this.locationsByGroup).flat();

    this.updateMultiselectOptions(locations);

    // After updating multiselect options, sync the cards with the new selections
    const selectedIds = this.multiselectOutlet.getSelectedValues();
    this.syncLocationCards(selectedIds);
  }

  syncLocationCards(selectedIds) {
    const existingCards = Array.from(this.cardsContainerTarget.children);

    // Hide/show existing cards
    existingCards.forEach((card) => {
      const locationId = card.dataset.locationId || card.id?.split("-")[2];
      if (!locationId) return;

      const shouldShow = selectedIds.includes(locationId);

      if (shouldShow) {
        card.hidden = false;
        this.setDestroyFlag(card, false);
      } else {
        // Check if this is a saved phone_location
        const phoneLocationIdField = card.querySelector(
          'input[name*="phone_locations_attributes"][name*="][id]"]:not([name*="location_attributes"])'
        );

        if (phoneLocationIdField?.value) {
          // Saved phone_location - hide and mark for destroy
          card.hidden = true;
          this.setDestroyFlag(card, true);
        } else {
          // Unsaved phone_location - remove entirely
          card.remove();
        }
      }
    });

    // Add new cards for newly selected locations
    const existingIds = existingCards
      .map((card) => card.dataset.locationId || card.id?.split("-")[2])
      .filter(Boolean);
    const newIds = selectedIds.filter((id) => !existingIds.includes(id));

    newIds.forEach((id) => this.addLocationCard(id));
  }

  addLocationCard(locationId) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");

    fetch(`/admin/support_agent/phones/location_card`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({
        location_id: locationId,
        phone_id: this.phoneIdValue,
        index: this.cardsContainerTarget.children.length,
      }),
    })
      .then((response) => response.text())
      .then((html) => {
        const wrapper = document.createElement("div");
        wrapper.dataset.locationId = locationId;
        wrapper.id = `location-id-${locationId}`;
        wrapper.innerHTML = html;
        this.cardsContainerTarget.appendChild(wrapper);
      })
      .catch((error) => {
        console.error("Error adding location card:", error);
      });
  }

  setDestroyFlag(card, destroy) {
    const destroyInput = card.querySelector('input[name*="_destroy"]');
    if (destroyInput) destroyInput.value = destroy ? "1" : "0";
  }

  updateMultiselectOptions(locations) {
    const options = locations
      .map((loc) => ({ label: loc.name, value: loc.id.toString() }))
      .sort((a, b) => a.label.localeCompare(b.label));

    this.multiselectOutlet.setOptions(options, true);
  }
}
